import { useCallback } from "react";
import {
  useExportTransactionsPdf,
  useExportManagementTransactionsPdf,
  useExportTransactionsExcel,
  useExportManagementTransactionsExcel,
} from "../../../hooks/queries/useTransactionQueries";
import { toastService } from "../../../providers";

const downloadBlob = (blob, filename) => {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

export const useTransactionExport = ({
  isSuperAdmin,
  searchQuery,
  dateRange,
  serviceCodes,
  merchantId,
  page = 0,
  size = 10,
} = {}) => {
  const regularParams = {
    searchQuery: searchQuery || "",
    startDate: dateRange?.startDate || "",
    endDate: dateRange?.endDate || "",
    serviceCodes: serviceCodes?.length ? serviceCodes : [],
  };

  const managementParams = {
    ...regularParams,
    merchantId: merchantId || "",
    page,
    size,
  };

  const regularPdf = useExportTransactionsPdf(regularParams);
  const managementPdf = useExportManagementTransactionsPdf(managementParams);
  const regularExcel = useExportTransactionsExcel(regularParams);
  const managementExcel = useExportManagementTransactionsExcel(managementParams);

  const exportPdf = isSuperAdmin ? managementPdf.mutate : regularPdf.mutate;
  const exportExcel = isSuperAdmin
    ? managementExcel.mutate
    : regularExcel.mutate;

  const isExporting =
    regularPdf.isPending ||
    managementPdf.isPending ||
    regularExcel.isPending ||
    managementExcel.isPending;

  const handleExportPdf = useCallback(() => {
    toastService.info("Exporting PDF. Your download will begin shortly...");

    exportPdf(undefined, {
      onSuccess: (response) => {
        downloadBlob(
          response.data,
          `transactions_${new Date().toISOString().slice(0, 10)}.pdf`,
        );
        toastService.success("Export complete! PDF download started.");
      },
      onError: (_err) => {
        toastService.error("Failed to export PDF");
      },
    });
  }, [exportPdf]);

  const handleExportExcel = useCallback(() => {
    toastService.info("Exporting Excel. Your download will begin shortly...");

    exportExcel(undefined, {
      onSuccess: (response) => {
        downloadBlob(
          response.data,
          `transactions_${new Date().toISOString().slice(0, 10)}.xlsx`,
        );
        toastService.success("Export complete! Excel download started.");
      },
      onError: (_err) => {
        toastService.error("Failed to export Excel");
      },
    });
  }, [exportExcel]);

  return {
    handleExportPdf,
    handleExportExcel,
    isExporting,
  };
};

export default useTransactionExport;
